import { Record, List } from 'immutable';
import { map, flatten, concat, filter, includes, union, unionBy } from 'lodash';

import { ITEM_FLAG, DATA_TYPES, SUPPLIER_OPTION } from 'commons-vehicle/constants/dataTypes.js';
import { EXTERIOR_COLOUR, INTERIOR_TRIM } from 'commons-vehicle/Quote/constants/modelTrimItemCategories.js';
import { IMAGE_STATUS } from 'commons-vehicle/ModelYear/constants/exteriorColourStatuses.js';
import DriveTrainModel from 'commons-vehicle/ModelYear/models/DriveTrainModel.js';
import EngineModel from 'commons-vehicle/ModelYear/models/EngineModel.js';
import TransmissionModel from 'commons-vehicle/ModelYear/models/TransmissionModel.js';
import PurchaseMethodsModel from 'commons-vehicle/PurchaseMethods/models/PurchaseMethodsModel.js';

const ModelTrimConfigurationRecord = Record({
    trimsBitField: 0,
    trimsBitFields: [],
    selectionCode: '',
    enabled: true,
    visible: true,
    selected: false,
    userSelected: false,
    code: '',
    description: '',
    id: 0,
    rank: 0,
    modelTrimCode: '',
    modelTrimCodeId: '',
    modelTrimRank: 0,
    packageSelectionCode: '',
    packageCode: '',
    modelTrim: {
        trimsBitField: 0,
        trimsBitFields: [],
        selectionCode: '',
        code: '',
        description: '',
        extensionCode: '',
        extensionDescription: '',
    },
    package: {
        code: '',
        description: '',
    },
    configurationToken: '',
    cabSizeSelectionCode: '',
    cabSize: null,
    boxSizeSelectionCode: '',
    boxSize: null,
    driveTrainSelectionCode: '',
    driveTrain: new DriveTrainModel(),
    engine: new EngineModel(),
    transmission: new TransmissionModel(),
    purchaseMethods: new PurchaseMethodsModel(),
    seating: 0,
    fuelCity: 0.0,
    fuelHighway: 0.0,
    modelTrimFeatures: {},
    features: {},
    featureIcons: List([]),
    nonModelTrimFeatureIconsCount: 0,
    configurationOptions: [],
    exteriorColourStatus: IMAGE_STATUS.NO_IMAGE,
    exteriorColours: List([]),
    interiorColours: List([]),
    options: [],
    smNewTrimCarId: 0,
    smNewCarId: 0,
    configurationItemsAreLoaded: false,
    optionsTotalRetail: 0,
    optionsTotalResidualRetail: 0,
});

class ModelTrimConfiguration extends ModelTrimConfigurationRecord {
    getSelectedExteriorColour() {
        const availableExteriorColours = this.exteriorColours.filter((colour) => colour.selected);
        if (availableExteriorColours.size === 1) {
            return availableExteriorColours.first();
        }
        return '';
    }

    getSelectedInteriorColour() {
        const availableInteriorColours = this.interiorColours.filter((colour) => colour.selected);
        if (availableInteriorColours.size === 1) {
            return availableInteriorColours.first();
        }
        return '';
    }

    getSelectedOptions() {
        return flatten(
            map(this.options, (option) => {
                return option.getSelectedItemsAndPackages();
            })
        );
    }

    getOptions() {
        return flatten(
            map(this.options, (option) => {
                return concat(option.itemsPackages, option.items);
            })
        );
    }

    getExteriorColourOptions() {
        const exteriorColourItems = filter(this.options, (option) => {
            return option.id === EXTERIOR_COLOUR;
        });
        return flatten(
            map(exteriorColourItems, (option) => {
                return concat(option.itemsPackages, option.items);
            })
        );
    }

    getInteriorColourOptions() {
        const exteriorColourItems = filter(this.options, (option) => {
            return option.id === INTERIOR_TRIM;
        });
        return flatten(
            map(exteriorColourItems, (option) => {
                return concat(option.itemsPackages, option.items);
            })
        );
    }

    getValidationSelectedOptions() {
        const selectedExteriorColour = this.getSelectedExteriorColour();
        const selectedInteriorColour = this.getSelectedInteriorColour();
        const isCashPurchase = this.purchaseMethods.cashPurchase.selected;
        const selectedOptions = map(this.getSelectedOptions(), (option) => {
            return option.getValidationOption(isCashPurchase);
        });

        const selectedItems = [];
        if (selectedExteriorColour) {
            selectedItems.push({
                code: selectedExteriorColour.colourCode,
                supplier: SUPPLIER_OPTION,
                flag: ITEM_FLAG.SELECTED | ITEM_FLAG.USER_SELECTED,
                amounts: {
                    retail: selectedExteriorColour.retail,
                    residualizingAmount: selectedExteriorColour.residualRetail,
                },
            });
        }
        if (selectedInteriorColour) {
            selectedItems.push({
                code: selectedInteriorColour.colourCode,
                supplier: SUPPLIER_OPTION,
                flag: ITEM_FLAG.SELECTED | ITEM_FLAG.USER_SELECTED,
                amounts: {
                    retail: selectedInteriorColour.retail,
                    residualizingAmount: selectedInteriorColour.residualRetail,
                },
            });
        }

        return unionBy(selectedOptions, selectedItems, 'code');
    }

    getConfigurationOptionsSelectionCodes() {
        return map(this.configurationOptions, (configurationOption) => {
            return configurationOption.selectionCode;
        });
    }

    getOptionsSelectionCodes() {
        const selectedExteriorColour = this.getSelectedExteriorColour();
        const selectedInteriorColour = this.getSelectedInteriorColour();
        const selectedOptions = map(this.getSelectedOptions(), (option) => {
            return option.selectionCode;
        });

        const selectedItems = [];
        if (selectedExteriorColour) {
            selectedItems.push(selectedExteriorColour.getColourSelectionCode());
        }
        if (selectedInteriorColour) {
            selectedItems.push(selectedInteriorColour.getColourSelectionCode());
        }

        return union(selectedOptions, selectedItems);
    }

    getPurchaseMethodsSelectedOptions() {
        const configurationOptionsCodes = map(this.configurationOptions, (configurationOption) => {
            return configurationOption.selectionCode;
        });
        return filter(this.purchaseMethods.selectedOptions, (option) => {
            return (
                option.dataType === DATA_TYPES.FEATURE.DATA_TYPE_ID &&
                (option.typeID < DATA_TYPES.FEATURE.TYPES.MODEL_TRIM ||
                    option.typeID > DATA_TYPES.FEATURE.TYPES.INTERIOR_TRIM) &&
                !includes(configurationOptionsCodes, option.selectionCode)
            );
        });
    }

    getEmployeeRebate() {
        return this.purchaseMethods.getEmployeeRebate();
    }
}

export default ModelTrimConfiguration;



// WEBPACK FOOTER //
// ./src/ModelYear/models/ModelTrimConfigurationModel.js